import { NotLoggedInError, NoWorkspaceError } from '../errors'
import { readConfig, writeConfig } from './config'
import { deleteToken, ensureGitignore, readToken, writeToken } from './token'

// The two files above, read as one thing: is this project connected, and to what.

export interface StoredAuth {
  serverUrl: string
  workspaceId: string
  workspaceName: string
  deployToken: string
}

/** What is missing, when something is. The two gaps have different causes and different fixes. */
export type AuthGap = 'not-logged-in' | 'no-workspace'

/** What a completed login has to write down. `workspaceName` is cosmetic and an older console may not send it. */
export interface AuthToSave {
  serverUrl: string
  workspaceId: string
  deployToken: string
  workspaceName?: string
}

export function saveAuth(
  { serverUrl, workspaceId, deployToken, workspaceName }: AuthToSave,
  projectRoot: string = process.cwd()
): void {
  writeConfig({ ...readConfig(projectRoot), serverUrl, workspaceId, ...(workspaceName ? { workspaceName } : {}) }, projectRoot)
  writeToken(deployToken, projectRoot)
  ensureGitignore(projectRoot)
}

export function loadAuth(projectRoot: string = process.cwd()): StoredAuth | null {
  const config = readConfig(projectRoot)
  const token = readToken(projectRoot)

  if (!token || !config.serverUrl) {
    return null
  }

  return {
    serverUrl    : config.serverUrl,
    workspaceId  : config.workspaceId || '',
    workspaceName: config.workspaceName || '',
    deployToken  : token,
  }
}

export function clearAuth(projectRoot: string = process.cwd()): void {
  deleteToken(projectRoot)
}

/** Which half of the connection is missing, or null when the project is fully connected. */
export function authGap(stored: StoredAuth | null): AuthGap | null {
  if (!stored || !stored.serverUrl || !stored.deployToken) {
    return 'not-logged-in'
  }

  if (!stored.workspaceId) {
    return 'no-workspace'
  }

  return null
}

/**
 * The credentials, or a thrown error naming what is absent.
 *
 * Deliberately does nothing about the gap. Offering a login is an interaction, and an operation that opens
 * a browser on its own cannot be called from a program, a test or a CI job — so the offer lives in `cli/`
 * and this stays a question with two possible answers.
 */
export function requireAuth(projectRoot: string = process.cwd()): StoredAuth {
  const stored = loadAuth(projectRoot)
  const gap = authGap(stored)

  if (gap === 'not-logged-in') {
    throw new NotLoggedInError()
  }

  if (gap === 'no-workspace') {
    throw new NoWorkspaceError()
  }

  return stored as StoredAuth
}

/**
 * How to refer to the connected workspace in output: its name when the console gave us one, else the id.
 *
 * The full form states the connection — name *and* id, since the id is what lives in `flowrunner.json`. The
 * short form is for every mention after that: repeating a GUID three times in one screen of output is noise
 * that makes the name harder to notice, not easier.
 */
export function describeWorkspace(
  { workspaceId, workspaceName }: { workspaceId: string; workspaceName?: string },
  { short = false }: { short?: boolean } = {}
): string {
  if (!workspaceName) {
    return workspaceId
  }

  return short ? workspaceName : `"${ workspaceName }" (${ workspaceId })`
}
